import type {
  InfiniteData,
  InfiniteQueryObserverOptions,
  QueryClient,
  QueryObserverOptions,
} from '@tanstack/query-core';
import type { LadderResponse, LadderRow, RankedProfileResponse } from '../../shared/api';
import { uniqueBy } from '../../shared/collections';
import { QueryHttpError } from '../api/http';
import { readLadderPage, readRankedProfile } from '../api/ranked';
import { LIST_GC_TIME, LIST_STALE_TIME } from './leaderboard';

type RankedProfileKey = readonly ['ranked-profile', number];
type LadderKey = readonly ['ladder', number];

export function rankedProfileQueryOptions(
  credential: string | null,
  profileGeneration: number,
): QueryObserverOptions<
  RankedProfileResponse,
  QueryHttpError,
  RankedProfileResponse,
  RankedProfileResponse,
  RankedProfileKey
> {
  const requestCredential = credential;
  return {
    queryKey: ['ranked-profile', profileGeneration],
    queryFn: ({ signal }) => {
      if (!requestCredential) throw new QueryHttpError('Your rank is unavailable.', 401);
      return readRankedProfile(requestCredential, signal);
    },
    enabled: Boolean(requestCredential),
    staleTime: LIST_STALE_TIME,
    gcTime: LIST_GC_TIME,
  };
}

export function ladderQueryOptions(
  credential: string | null,
  profileGeneration: number,
): InfiniteQueryObserverOptions<
  LadderResponse,
  QueryHttpError,
  InfiniteData<LadderResponse>,
  LadderKey,
  number
> {
  return {
    queryKey: ['ladder', profileGeneration],
    queryFn: ({ pageParam, signal }) => readLadderPage({ offset: pageParam, signal }, credential),
    initialPageParam: 0,
    getNextPageParam: (page) => page.nextOffset ?? undefined,
    staleTime: LIST_STALE_TIME,
    gcTime: LIST_GC_TIME,
  };
}

export function ladderRows(data: InfiniteData<LadderResponse> | undefined): LadderRow[] {
  return uniqueBy(
    (data?.pages ?? []).flatMap((page) => page.rows),
    (row) => row.profileId,
  );
}

/** Refetch page one atomically so failure leaves all currently visible pages cached. */
export async function refreshLadder(
  client: QueryClient,
  credential: string | null,
  profileGeneration: number,
): Promise<void> {
  await client.infiniteQuery({
    ...ladderQueryOptions(credential, profileGeneration),
    pages: 1,
    staleTime: 0,
  });
}
